import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Send, Instagram, Facebook, Loader } from 'lucide-react';
import { useLanguage } from '../utils/LanguageContext';
import { useAboutData } from "../hooks/useAboutData";
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = 'https://texla.uz/api/v1';

const getLocalizedText = (obj, language) => {
    if (!obj) return '';
    const key = `title_${language}`;
    return obj[key] || obj.title_uz || obj.title || '';
};

// Telegram Floating Button Component
const TelegramFloatingButton = ({ telegramLink }) => {
    return (
        <a
            href="https://t.me/texlab_uz"
            target="_blank"
            rel="noopener noreferrer"
            className="fixed bottom-4 right-4 sm:bottom-10 sm:right-10 z-50 w-14 h-14 sm:w-20 sm:h-20 bg-[#0088cc] rounded-full flex items-center justify-center shadow-lg hover:bg-[#006699] transition-all duration-300 hover:scale-110 animate-bounce-slow group"
            aria-label="Telegram"
        >
            <Send size={20} className="sm:hidden text-white group-hover:rotate-12 transition-transform" />
            <Send size={30} className="hidden sm:block text-white group-hover:rotate-12 transition-transform" />
            <span className="absolute w-full h-full rounded-full bg-[#0088cc] animate-ping opacity-30"></span>
        </a>
    );
};

const Footer = () => {
    const { about } = useAboutData();
    const { language, t } = useLanguage();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/categories/`);
            setCategories(response.data || []);
        } catch (error) {
            setCategories([]);
        }
    };

    const handleCategoryClick = (categoryId) => {
        window.scrollTo(0, 0);
        navigate(`/shop?category=${categoryId}`);
    };

    if (!about) {
        return (
            <footer className="bg-gray-900 text-white py-8 lg:py-12">
                <div className="max-w-7xl mx-auto px-4 flex items-center justify-center h-40">
                    <Loader className="animate-spin" size={32} />
                </div>
            </footer>
        );
    }

    return (
        <>
            {/* Telegram Floating Button */}
            {about.telegram && <TelegramFloatingButton telegramLink={about.telegram} />}

            <footer className="bg-gray-900 text-white py-8 lg:py-12">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-8">
                        {/* Logo and Contact */}
                        <div>
                            <Link to={"/"}>
                                <div className="flex items-center gap-2 mb-6">
                                    {about.logo && (
                                        <img
                                            src={about.logo}
                                            alt="Logo"
                                            className="w-16 h-16 lg:w-24 lg:h-24 rounded object-contain bg-white p-4"
                                        />
                                    )}
                                </div>
                            </Link>
                            <div className="space-y-3 text-sm lg:text-base">
                                <a
                                    href={`tel:${about.phone}`}
                                    className="flex items-center gap-2 hover:text-gray-300 transition font-bold"
                                >
                                    <Phone size={18} />
                                    {about.phone}
                                </a>
                                <div className="flex items-start gap-2">
                                    <MapPin size={18} className="flex-shrink-0 mt-1" />
                                    <span className="text-xs lg:text-sm hover:text-gray-300 transition">
                                        {language === 'ru'
                                            ? about.address_ru
                                            : about.address_uz}
                                    </span>
                                </div>
                                <a
                                    href={`mailto:${about.email}`}
                                    className="flex items-center gap-2 hover:text-gray-300 transition"
                                >
                                    <Mail size={18} />
                                    {about.email}
                                </a>
                            </div>
                            <div className="flex gap-3 mt-4">
                                {about.telegram && (
                                    <a
                                        href={about.telegram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition"
                                    >
                                        <Send size={18} />
                                    </a>
                                )}
                                {about.instagram && (
                                    <a
                                        href={about.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition"
                                    >
                                        <Instagram size={18} />
                                    </a>
                                )}
                                {about.facebook && (
                                    <a
                                        href={about.facebook}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition"
                                    >
                                        <Facebook size={18} />
                                    </a>
                                )}
                            </div>
                        </div>

                        {/* About Section */}
                        <div>
                            <h5 className="font-bold text-lg lg:text-base mb-4">{t('footer.aboutUs')}</h5>
                            <div className="space-y-2 text-xs lg:text-sm">
                                <Link to="/about" className="block hover:text-gray-300 transition">{t('header.aboutCompany')}</Link>
                                <Link to="/blog" className="block hover:text-gray-300 transition">{t('header.news')}</Link>
                                <Link to="/faqs" className="block hover:text-gray-300 transition">{t('header.faqs')}</Link>
                                <Link to="/contact" className="block hover:text-gray-300 transition">{t('header.contact')}</Link>
                            </div>
                        </div>

                        {/* Catalog Section */}
                        <div>
                            <h5 className="font-bold text-lg lg:text-base mb-4">{t('header.catalog')}</h5>
                            <div className="space-y-2 text-xs lg:text-sm">
                                {categories.length > 0 ? (
                                    categories.slice(0, 5).map(cat => (
                                        <button
                                            key={cat.id}
                                            onClick={() => handleCategoryClick(cat.id)}
                                            className="block hover:text-gray-300 transition text-left"
                                        >
                                            {getLocalizedText(cat, language)}
                                        </button>
                                    ))
                                ) : (
                                    <>
                                        <a href="#" className="block hover:text-gray-300 transition">{t('categories.analytical')}</a>
                                        <a href="#" className="block hover:text-gray-300 transition">{t('categories.testing')}</a>
                                        <a href="#" className="block hover:text-gray-300 transition">{t('categories.medical')}</a>
                                        <a href="#" className="block hover:text-gray-300 transition">{t('categories.furniture')}</a>
                                        <a href="#" className="block hover:text-gray-300 transition">{t('categories.consumables')}</a>
                                    </>
                                )}
                            </div>
                        </div>

                    </div>

                    <div className="border-t border-gray-700 pt-6 text-center text-xs lg:text-sm text-gray-400">
                        <p>{t('footer.copyright')}</p>
                    </div>
                </div>
            </footer>

            {/* Custom animation styles */}
            <style>{`
                @keyframes bounce-slow {
                    0%, 100% {
                        transform: translateY(0);
                    }
                    50% {
                        transform: translateY(-10px);
                    }
                }
                .animate-bounce-slow {
                    animation: bounce-slow 2s ease-in-out infinite;
                }
            `}</style>
        </>
    );
};

export default Footer;